class DeliveryFrequencyDropdown extends HTMLElement {
    static get observedAttributes() {
      return ['disabled'];
    }
  
    constructor() {
      super();
      this.handleSelectChange = this.handleSelectChange.bind(this);
      this.handleOptionClick = this.handleOptionClick.bind(this);
      this.handleToggleClick = this.handleToggleClick.bind(this);
      this.handleDocumentClick = this.handleDocumentClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handlePaymentOptionsChange = this.handlePaymentOptionsChange.bind(this);
      this._activeIndex = 0;
      this.getCurrentSelection = this.getCurrentSelection.bind(this);
    }
  
    connectedCallback() {
      this.productId = this.getAttribute('product-id') || null;
      this.productBasePriceInt = this.getAttribute('product-base-price-int') || null;
      this.increment = this.getAttribute('increment') || null;
      this._label = this.querySelector('label');
      this._select = this.querySelector('select');
      this._wrapper =
        this.querySelector('.df-select-wrapper') ||
        this._select?.closest('.df-select-wrapper');
      this._list = this.querySelector('.df-list');
      this._options = this._list ? Array.from(this._list.querySelectorAll('.df-option')) : [];
  
      // NEW: visible selected text in the top bar
      this._displayLabel = this.querySelector('.df-select-display-label');
      this._displayPrice = this.querySelector('.df-select-display-price');
  
      if (!this._select || !this._wrapper || !this._list) return;

      /* --- ARIA & accessibility wiring --- */
  
      // Native select: hidden from AT, still used for form submission.
      this._select.setAttribute('tabindex', '-1');
      this._select.setAttribute('aria-hidden', 'true');
  
      // Combobox wrapper
      this._wrapper.setAttribute('role', 'combobox');
      this._wrapper.setAttribute('aria-haspopup', 'listbox');
      this._wrapper.setAttribute('aria-expanded', 'false');
      this._wrapper.setAttribute(
        'aria-controls',
        this._list.id || 'delivery-frequency-list'
      );
      this._wrapper.setAttribute('tabindex', '0');
  
      if (this._label) {
        if (!this._label.id) {
          this._label.id = 'delivery-frequency-label';
        }
        this._wrapper.setAttribute('aria-labelledby', this._label.id);
      }
  
      // Listbox
      this._list.setAttribute('role', 'listbox');
      this._list.setAttribute('aria-hidden', 'true');
  
      // Options
      this._options.forEach((li, index) => {
        if (!li.id) {
          li.id = `delivery-frequency-option-${index}`;
        }
        li.setAttribute('role', 'option');
        li.setAttribute('aria-selected', 'false');
      });
  
      /* --- event listeners --- */
      this._select.addEventListener('change', this.handleSelectChange);
      this._wrapper.addEventListener('click', this.handleToggleClick);
      this._wrapper.addEventListener('keydown', this.handleKeydown);
      this._options.forEach((li) => {
        li.addEventListener('click', this.handleOptionClick);
      });
      document.addEventListener('click', this.handleDocumentClick);
      // Listen for changes from payment options selector to toggle visibility
      document.addEventListener('payment-options:change', this.handlePaymentOptionsChange, { passive: true });
      // Set initial visibility based on current payment option state (if available)
      const selectorId = this.productId ? `payment-options-selector-${this.productId}` : null;
      const selectorEl = selectorId ? document.getElementById(selectorId) : null;
      if (selectorEl) {
        const isOneTimeChecked = !!selectorEl.querySelector('#one-time-option')?.checked;
        this.updateVisibility(isOneTimeChecked);
      }
  
      this.updateDisabledState();
      this.syncFromSelect(false);
    }
  
    disconnectedCallback() {
      if (this._select) {
        this._select.removeEventListener('change', this.handleSelectChange);
      }
      if (this._wrapper) {
        this._wrapper.removeEventListener('click', this.handleToggleClick);
        this._wrapper.removeEventListener('keydown', this.handleKeydown);
      }
      this._options.forEach((li) => {
        li.removeEventListener('click', this.handleOptionClick);
      });
      document.removeEventListener('click', this.handleDocumentClick);
      document.removeEventListener('payment-options:change', this.handlePaymentOptionsChange);
    }
  
    attributeChangedCallback(name, _oldValue, _newValue) {
      if (!this.isConnected) return;
      if (name === 'disabled') this.updateDisabledState();
    }
  
    get disabled() {
      return this.hasAttribute('disabled');
    }
  
    set disabled(value) {
      if (value) this.setAttribute('disabled', '');
      else this.removeAttribute('disabled');
    }
  
    get value() {
      return this._select?.value ?? null;
    }
  
    set value(val) {
      if (!this._select) return;
      this._select.value = val;
      this.syncFromSelect(true);
    }
  
    /* ─── core helpers ─────────────────────── */
  
    setOpen(isOpen) {
      if (!this._wrapper || !this._list) return;
  
      if (isOpen && this.disabled) {
        isOpen = false;
      }
  
      if (isOpen) {
        this.setAttribute('data-open', 'true');
        this._wrapper.setAttribute('aria-expanded', 'true');
        this._list.setAttribute('aria-hidden', 'false');
      } else {
        this.removeAttribute('data-open');
        this._wrapper.setAttribute('aria-expanded', 'false');
        this._list.setAttribute('aria-hidden', 'true');
      }
    }
  
    updateDisabledState() {
      const isDisabled = this.disabled;
      if (this._select) this._select.disabled = isDisabled;
      if (this._wrapper) {
        this._wrapper.setAttribute('aria-disabled', isDisabled ? 'true' : 'false');
        this._wrapper.tabIndex = isDisabled ? -1 : 0;
      }
    }
  
    syncFromSelect(emit = true) {
      if (!this._select || !this._options.length) {
        if (emit) this.emitChange();
        return;
      }
  
      const selectedIndex = this._select.selectedIndex;
      this._activeIndex = selectedIndex >= 0 ? selectedIndex : 0;
  
      this._options.forEach((li, index) => {
        const selected = index === selectedIndex;
        li.classList.toggle('df-option--selected', selected);
        li.setAttribute('aria-selected', selected ? 'true' : 'false');
      });
  
      // NEW: update visible "label + price" in the top bar
      const optionEl = this._select.options[selectedIndex];
      const li = this._options[selectedIndex];
      const priceEl = li?.querySelector('.df-option-price');
  
      const labelText = optionEl ? optionEl.textContent.trim() : '';
      const priceText = priceEl ? priceEl.textContent.trim() : '';
  
      if (this._displayLabel) {
        this._displayLabel.textContent = labelText;
      }
      if (this._displayPrice) {
        this._displayPrice.textContent = priceText;
      }
  
      // ARIA active-descendant
      if (this._wrapper && this._options[this._activeIndex]) {
        this._wrapper.setAttribute(
          'aria-activedescendant',
          this._options[this._activeIndex].id
        );
      }
  
      if (emit) this.emitChange();
    }
  
    /* ─── event handlers ───────────────────── */
  
    handleSelectChange() {
      if (this.disabled) return;
      this.syncFromSelect(true);
    }
  
    handleOptionClick(event) {
      if (this.disabled) return;
  
      const li = event.currentTarget;
      const index = this._options.indexOf(li);
      if (index === -1 || !this._select) return;
  
      this._select.selectedIndex = index;
      this._select.dispatchEvent(new Event('change', { bubbles: true }));
      this.setOpen(false);
    }
  
    handleToggleClick(event) {
      if (this.disabled) return;
      event.stopPropagation();
      const isOpen = this.getAttribute('data-open') === 'true';
      this.setOpen(!isOpen);
    }
  
    handleDocumentClick(event) {
      if (!this.contains(event.target)) {
        this.setOpen(false);
      }
    }
  
    handleKeydown(event) {
      if (this.disabled) return;
  
      const key = event.key;
      const isOpen = this.getAttribute('data-open') === 'true';
  
      switch (key) {
        case 'ArrowDown':
        case 'Down': {
          event.preventDefault();
          if (!isOpen) this.setOpen(true);
          this.moveActive(1);
          break;
        }
        case 'ArrowUp':
        case 'Up': {
          event.preventDefault();
          if (!isOpen) this.setOpen(true);
          this.moveActive(-1);
          break;
        }
        case 'Enter':
        case ' ': {
          event.preventDefault();
          if (!isOpen) {
            this.setOpen(true);
          } else {
            // confirm current active selection
            this.commitActiveSelection();
            this.setOpen(false);
          }
          break;
        }
        case 'Escape':
        case 'Esc': {
          event.preventDefault();
          this.setOpen(false);
          break;
        }
        default:
          break;
      }
    }
  
    moveActive(delta) {
      if (!this._options.length || !this._select) return;
  
      let next = this._activeIndex + delta;
      if (next < 0) next = 0;
      if (next >= this._options.length) next = this._options.length - 1;
      this._activeIndex = next;
  
      this._select.selectedIndex = this._activeIndex;
      this.syncFromSelect(true);
  
      const li = this._options[this._activeIndex];
      if (li) {
        li.scrollIntoView({ block: 'nearest' });
      }
    }
  
    commitActiveSelection() {
      if (!this._select) return;
      this._select.selectedIndex = this._activeIndex;
      this._select.dispatchEvent(new Event('change', { bubbles: true }));
    }

    getCurrentSelection() {
      const index = this._select.selectedIndex;
      const optionEl = this._select.options[index];
      const li = this._options[index];
  
      const priceEl = li?.querySelector('.df-option-price');
      const descEl = li?.querySelector('.df-option-label-sub');
  
      const detail = {
        value: optionEl?.value ?? null,
        maxQuantityMultiplier: optionEl?.getAttribute('max-quantity-multiplier') ?? 1,
        label: optionEl?.textContent?.trim() ?? '',
        price: optionEl?.getAttribute('price') ?? null,
        index,
        sellingPlanId: optionEl?.getAttribute('selling-plan-id') ?? null,
        frequencyProductId: optionEl?.getAttribute('frequency-product-id') ?? null,
        frequencyProductVariantId: optionEl?.getAttribute('frequency-product-variant-id') ?? null,
        priceText: priceEl ? priceEl.textContent.trim() : '',
        description: descEl ? descEl.textContent.trim() : '',
        productBasePriceInt: this.productBasePriceInt,
        productBaseQuantity: optionEl?.getAttribute('base-quantity-multiplier') ?? 1, // Non-bundle products default to 1
        productSelectedPriceInt: optionEl?.getAttribute('selected-price-int') ?? null,
        increment: this.increment,
      };

      return detail;
    }
  
    /* ─── custom event payload ─────────────── */
  
    emitChange() {
      if (!this._select) return;
  
      const detail = this.getCurrentSelection();
  
      this.dispatchEvent(
        new CustomEvent('delivery-frequency:change', {
          bubbles: true,
          composed: true,
          detail
        })
      );
    }

    /* ─── integration with payment options ─── */
    updateVisibility(isOneTime) {
      if (isOneTime) {
        this.setAttribute('hidden', '');
      } else {
        this.removeAttribute('hidden');
      }
    }

    handlePaymentOptionsChange(event) {
      const detail = event?.detail || {};
      // If we have product scoping, ignore unrelated events
      if (this.productId && detail.productId && String(detail.productId) !== String(this.productId)) {
        return;
      }

      if (detail.isOneTime) {
        if (this._select) {
          this._select.selectedIndex = 0;
          this._activeIndex = 0;
        }

        this.setOpen(false);
        this.syncFromSelect(true);
      }

      this.updateVisibility(!!detail.isOneTime);
    }
  }
  
  if (!customElements.get('delivery-frequency-dropdown')) {
    customElements.define('delivery-frequency-dropdown', DeliveryFrequencyDropdown);
  }
  
  // Example listener:
  document.addEventListener('DOMContentLoaded', () => {
    const dropdown = document.querySelector('delivery-frequency-dropdown');
    if (!dropdown) return;
    dropdown.addEventListener('delivery-frequency:change', (event) => {
      console.log('Delivery frequency updated', event.detail);
    });
  });
  
